import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { PhysicalStoreModelsTransformer } from '../transformers/store-locator.transformer';

import { PhysicalStoreDetail, PhysicalStoreRequestConfiguration } from './models/physical-store.models';

import { ApiPhysicalStoreService } from '@api/_api/api-store-locator/api-physical-store/api-physical-store.service';
import { ApiPhysicalStore, ApiPhysicalStoreByCountry, ApiPhysicalStoreDetail } from '@api/_api/api-store-locator/api-physical-store/models/api-physical-store.models';
import { GeolocationServices } from '@api/geolocation/services/geolocation.service';
import { RequestParams } from '@core/infrastructure/http-requests/http-requests.service';
import { getStoreAndLanguage, getStoreId } from '@core/infrastructure/state/core.selectors';
import { MapPublicUseCases } from '@shared/map';

/**
 * Servicio para interactuar con las tiendas del buscador de Tiendas
 */

@Injectable()
export class PhysicalStoreService {
    // Definición de propiedades
    public stores: PhysicalStoreDetail[];

    public relatedInfo: string;

    private requestParameters: RequestParams;

    constructor(
        private readonly apiPhysicalStoreService: ApiPhysicalStoreService,
        private readonly store: Store,
        private readonly geolocationService: GeolocationServices,
    ) {}

    /**
     * Función para obtener un listado de tiendas cercanas en base a una localización
     * @param requestConfiguration Configuración de la consulta
     * @return Objeto de tipo observable con un listado de tiendas
     */
    public getStores(requestConfiguration: PhysicalStoreRequestConfiguration): Observable<PhysicalStoreDetail[]> {
        // Definimos los parámetros de consulta

        return this.store.select(getStoreAndLanguage).pipe(
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
            map((storeaAndLang) => {
                const requestParameters: RequestParams = [
                    { key: 'languageId', value: storeaAndLang.languageId },
                    { key: 'latitude', value: requestConfiguration.latitude },
                    { key: 'longitude', value: requestConfiguration.longitude },
                    ... requestConfiguration.favouriteStores ? [{ key: 'favouriteStores', value: Boolean(requestConfiguration.favouriteStores).valueOf().toString() }] : [],
                    ... requestConfiguration.countryCode ? [{ key: 'countryCode', value: requestConfiguration.countryCode }] : [],
                    ... requestConfiguration.lastStores ? [{ key: 'lastStores', value: Boolean(requestConfiguration.lastStores).valueOf().toString() }] : [],
                    ... requestConfiguration.closerStores ? [{ key: 'closerStores', value: Boolean(requestConfiguration.closerStores).valueOf().toString() }] : [],
                    ... requestConfiguration.min ? [{ key: 'min', value: requestConfiguration.min ? requestConfiguration.min.toString() : '10' }] : [],
                    ... requestConfiguration.radioMax ? [{ key: 'radioMax', value: requestConfiguration.radioMax ? requestConfiguration.radioMax.toString() : '100' }] : [],
                    ... requestConfiguration.receiveEcommerce ? [{ key: 'receiveEcommerce', value: Boolean(requestConfiguration.receiveEcommerce).valueOf().toString() }] : [],
                    ... requestConfiguration.showBlockedMaxPackage ? [{ key: 'showBlockedMaxPackage', value: Boolean(requestConfiguration.showBlockedMaxPackage).valueOf().toString() }] : [],
                ];
                this.requestParameters = requestParameters;
                return { requestParameters, storeId: storeaAndLang.storeId };
            }),
            switchMap(
                ({ requestParameters, storeId }) => this.apiPhysicalStoreService.getStores(requestParameters, storeId),
            ),
            map((apiPhysicalStore: ApiPhysicalStore): PhysicalStoreDetail[] => {
                const stores = PhysicalStoreModelsTransformer.fromApi(apiPhysicalStore);
                this.stores = stores;
                return stores;
            }),
            first(),
        );
    }

    /**
     * Función para obtener un listado de tiendas dentro de un país
     * @param requestConfiguration Configuración de la consulta
     * @return Objeto de tipo observable con un listado de tiendas
     */
    public getStoresByCountry(country: string, calculateDistance = true): Observable<PhysicalStoreDetail[]> {
        const requestParameters: RequestParams = [
            ... country ? [{ key: 'countryCode', value: country }] : [],
        ];
        this.requestParameters = requestParameters;
        return this.store.select(getStoreId).pipe(
            switchMap((storeId) => {
                return forkJoin([
                    this.apiPhysicalStoreService.getPhysicalStoresByCountry(requestParameters, storeId).pipe(
                        map((apiPhysicalStore: ApiPhysicalStoreByCountry): PhysicalStoreDetail[] => {
                            return PhysicalStoreModelsTransformer.fromApiCountry(apiPhysicalStore);
                        }),
                    ).pipe(first()),
                    calculateDistance ? this.geolocationService.getCoordinates().pipe(first()) : of(null),
                ]);
            }),
            map(([stores, coords]) => {
                if (!coords) {
                    return stores;
                }
                // Calculate distance to each store inside country
                return stores.map((store: PhysicalStoreDetail) => {
                    const p1 = { // Store coords
                        latitude: Number(store.latitude),
                        longitude: Number(store.longitude),
                    };
                    const p2 = { // User coords
                        latitude: Number(coords.latitude),
                        longitude: Number(coords.longitude),
                    };
                    const newStore = { ...store };

                    newStore.distance = MapPublicUseCases.calcDistanceBetweenUseCase(p1, p2);

                    return newStore;
                });
            }),
            first(),
        );
    }

    public getStore(storeId: string, languageId: string, id: string): Observable<PhysicalStoreDetail> {
        return this.apiPhysicalStoreService
            .getStore(storeId, languageId, id)
            .pipe(
                map(
                    (apiPhysicalStore: ApiPhysicalStoreDetail): PhysicalStoreDetail =>
                        PhysicalStoreModelsTransformer.transformStore(apiPhysicalStore),
                ),
            );
    }
}
